import { useState } from 'react';
import { LayoutDashboard, Map, BarChart3, Settings, Wrench, Calendar, CheckSquare, BookOpen, HelpCircle, X, LogOut, Database, FileBarChart, Ship, FileText, MessageSquare, ChevronDown, ChevronRight, ClipboardList } from 'lucide-react';
import Dashboard from './components/dashboard/Dashboard';
import PortChecker from './components/port-checker/PortChecker';
import PortValidator from './components/port-checker/PortValidator';
import EngineeringWorklogs from './components/engineering-worklogs/EngineeringWorklogs';
import WorklogSummary from './components/engineering-worklogs/WorklogSummary';
import Missing7hrReport from './components/engineering-worklogs/Missing7hrReport';
import DatabaseBrowser from './components/database-browser/DatabaseBrowser';
import SecondaryDatabaseBrowser from './components/pcer-database-browser/PCERDatabaseBrowser';
import PCERReport from './components/reports/PCERReport';
import ReportsManager from './components/reports/ReportsManager';
import KnowledgeBase from './components/knowledge-base/KnowledgeBase';
import Login from './components/auth/Login';
import MultiDatabaseLogin from './components/auth/MultiDatabaseLogin';
import TableMapping from './components/auth/TableMapping';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePortsData } from './hooks/usePortsData';
import { useEngineeringData } from './hooks/useEngineeringData';
import { Button } from '@/components/ui/button';

// Separate component that only renders when connected
function ConnectedApp() {
  const [activeView, setActiveView] = useState('dashboard');
  const [helpOpen, setHelpOpen] = useState(false);
  const [databaseBrowserExpanded, setDatabaseBrowserExpanded] = useState(true);
  const [reportsExpanded, setReportsExpanded] = useState(true);
  const [activeReport, setActiveReport] = useState(null);
  const { disconnect, connectionInfo } = useAuth();
  const { portsData, countriesData, continentsData, regionsData, portRegionsData, timeZonesData, loading, refetch } = usePortsData();
  const { worklogsData, requestsData, tasksData, usersData, loading: engineeringLoading } = useEngineeringData();

  const handleDisconnect = async () => {
    await disconnect();
  };

  // Map activeView to relevant KB section
  const getHelpSection = (view) => {
    const mapping = {
      'dashboard': 'overview',
      'port-checker': 'port-checker',
      'port-validator': 'port-validator',
      'engineering-worklogs': 'engineering-worklogs',
      'worklog-summary': 'engineering-worklogs',
      'knowledge-base': 'overview',
      'analytics': 'overview',
      'settings': 'overview'
    };
    return mapping[view] || 'overview';
  };

  const navigation = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'port-checker', label: 'Port Checker', icon: Map },
    { id: 'port-validator', label: 'Port Validator', icon: CheckSquare },
    { id: 'engineering-worklogs', label: 'Engineering Worklogs', icon: Wrench },
    { id: 'worklog-summary', label: 'Worklog Summary', icon: Calendar },
    { id: 'missing-7hr-report', label: '7hr Missing Report', icon: Calendar },
    { 
      id: 'database-browser', 
      label: 'Database Browser', 
      icon: Database,
      hasSubItems: true,
      expandedState: 'databaseBrowserExpanded',
      subItems: [
        { id: 'database-browser', label: 'Core', icon: Database },
        { id: 'operations-db', label: 'Operations', icon: FileBarChart },
        { id: 'voyage-estimate-db', label: 'Voyage Estimate', icon: Ship },
        { id: 'reports-db', label: 'Reports', icon: FileText },
        { id: 'inquiry-db', label: 'Inquiry', icon: MessageSquare },
      ]
    },
    { 
      id: 'reports', 
      label: 'Reports', 
      icon: ClipboardList,
      hasSubItems: true,
      expandedState: 'reportsExpanded',
      subItems: [
        { id: 'reports-manager', label: 'Reports Manager', icon: ClipboardList },
        { id: 'pcer-report', label: 'PCER Report', icon: FileBarChart },
      ]
    },
    { id: 'knowledge-base', label: 'Knowledge Base', icon: BookOpen },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-gray-800 to-gray-900 text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold">COEMS</h2>
          <p className="text-sm text-white/80">Test Utility</p>
          {connectionInfo && (
            <div className="mt-2 text-xs text-white/60">
              <div className="truncate">📊 {connectionInfo.database}</div>
              <div className="truncate">🖥️ {connectionInfo.host}</div>
            </div>
          )}
        </div>
        <nav className="flex-1 p-4">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = item.hasSubItems 
              ? item.subItems.some(sub => sub.id === activeView)
              : activeView === item.id;
            
            return (
              <div key={item.id}>
                <Button
                  onClick={() => {
                    if (item.hasSubItems) {
                      const expandedState = item.expandedState;
                      if (expandedState === 'databaseBrowserExpanded') {
                        setDatabaseBrowserExpanded(!databaseBrowserExpanded);
                      } else if (expandedState === 'reportsExpanded') {
                        setReportsExpanded(!reportsExpanded);
                      }
                    } else {
                      setActiveView(item.id);
                    }
                  }}
                  variant="ghost"
                  className={`w-full justify-start gap-3 mb-2 ${
                    isActive
                      ? 'bg-white/20 border-l-4 border-white text-white hover:bg-white/30 hover:text-white'
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.hasSubItems && (
                    (item.expandedState === 'databaseBrowserExpanded' ? databaseBrowserExpanded : reportsExpanded) 
                      ? <ChevronDown className="w-4 h-4" /> 
                      : <ChevronRight className="w-4 h-4" />
                  )}
                </Button>
                
                {item.hasSubItems && 
                  ((item.expandedState === 'databaseBrowserExpanded' && databaseBrowserExpanded) ||
                   (item.expandedState === 'reportsExpanded' && reportsExpanded)) && (
                  <div className="ml-4 mb-2">
                    {item.subItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      return (
                        <Button
                          key={subItem.id}
                          onClick={() => setActiveView(subItem.id)}
                          variant="ghost"
                          className={`w-full justify-start gap-3 mb-1 ${
                            activeView === subItem.id
                              ? 'bg-white/20 border-l-4 border-white text-white hover:bg-white/30 hover:text-white'
                              : 'text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <SubIcon className="w-4 h-4" />
                          <span className="text-sm">{subItem.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Button
            onClick={handleDisconnect}
            variant="ghost"
            className="w-full justify-start gap-3 text-white/90 hover:bg-red-500/20 hover:text-white"
          >
            <LogOut className="w-5 h-5" />
            <span>Disconnect</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold capitalize">{activeView.replace('-', ' ')}</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHelpOpen(true)}
              className="flex items-center gap-2"
            >
              <HelpCircle className="w-4 h-4" />
              Help
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeView === 'dashboard' && (
            <div className="h-full overflow-auto">
              <Dashboard portsData={portsData} countriesData={countriesData} loading={loading} />
            </div>
          )}
          {activeView === 'port-checker' && (
            <PortChecker 
              portsData={portsData} 
              countriesData={countriesData}
              continentsData={continentsData}
              regionsData={regionsData}
              portRegionsData={portRegionsData}
              timeZonesData={timeZonesData}
              loading={loading}
              onRefresh={refetch}
            />
          )}
          {activeView === 'port-validator' && (
            <div className="h-full overflow-auto">
              <PortValidator 
                portsData={portsData}
                countriesData={countriesData}
                timeZonesData={timeZonesData}
              />
            </div>
          )}
          {activeView === 'engineering-worklogs' && (
            <EngineeringWorklogs
              worklogsData={worklogsData}
              requestsData={requestsData}
              tasksData={tasksData}
              usersData={usersData}
              loading={engineeringLoading}
            />
          )}
          {activeView === 'worklog-summary' && (
            <WorklogSummary
              worklogsData={worklogsData}
              requestsData={requestsData}
              tasksData={tasksData}
              usersData={usersData}
              loading={engineeringLoading}
            />
          )}
          {activeView === 'missing-7hr-report' && (
            <div className="h-full overflow-auto p-6">
              <Missing7hrReport />
            </div>
          )}
          {activeView === 'database-browser' && (
            <DatabaseBrowser />
          )}
          {activeView === 'operations-db' && (
            <SecondaryDatabaseBrowser dbKey="operations" dbLabel="Operations" />
          )}
          {activeView === 'voyage-estimate-db' && (
            <SecondaryDatabaseBrowser dbKey="voyage_estimate" dbLabel="Voyage Estimate" />
          )}
          {activeView === 'reports-db' && (
            <SecondaryDatabaseBrowser dbKey="reports" dbLabel="Reports" />
          )}
          {activeView === 'inquiry-db' && (
            <SecondaryDatabaseBrowser dbKey="inquiry" dbLabel="Inquiry" />
          )}
          {activeView === 'reports-manager' && (
            <ReportsManager 
              onOpenReport={(report) => {
                setActiveReport(report);
                setActiveView('pcer-report');
              }}
            />
          )}
          {activeView === 'pcer-report' && (
            <PCERReport 
              savedReport={activeReport}
              onSave={(reportState) => {
                if (!activeReport) return;
                
                // Update localStorage with new report state
                const savedReports = JSON.parse(localStorage.getItem('savedReports') || '[]');
                const updatedReports = savedReports.map(report => 
                  report.id === activeReport.id 
                    ? { ...report, ...reportState }
                    : report
                );
                localStorage.setItem('savedReports', JSON.stringify(updatedReports));
                
                // Update active report
                setActiveReport({ ...activeReport, ...reportState });
              }}
              onBack={() => {
                setActiveView('reports-manager');
                setActiveReport(null);
              }}
            />
          )}
          {activeView === 'knowledge-base' && (
            <KnowledgeBase />
          )}
          {(activeView === 'analytics' || activeView === 'settings') && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-3xl mb-4">{activeView === 'analytics' ? '' : ''}</h2>
                <p className="text-gray-600 capitalize">{activeView} coming soon...</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Help Overlay */}
      {helpOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={() => setHelpOpen(false)}
          />
          
          {/* Help Drawer */}
          <div className="fixed inset-y-0 right-0 w-[800px] bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-800 text-white">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Help & Documentation</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHelpOpen(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Knowledge Base Content */}
            <div className="flex-1 overflow-hidden">
              <KnowledgeBase 
                initialSection={getHelpSection(activeView)}
                overlayMode={true}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Main component that handles auth routing
function AppContent() {
  const { isConnected, setupComplete, completeSetup, disconnect, loading: authLoading } = useAuth();

  console.log('AppContent render:', { isConnected, setupComplete, authLoading });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return <MultiDatabaseLogin />;
  }

  if (!setupComplete) {
    return <TableMapping onComplete={completeSetup} onBack={disconnect} />;
  }

  return <ConnectedApp />;
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
